import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import {
  type AnalyticsProductTier,
  ClientAdProductType,
  MarketAnalyticsWebApiHandler_getMarketPackageDistribution,
  MarketAnalyticsWebApiHandler_getMarketPackageDistribution_Path,
} from '@zg-rentals/ts-customer-analytics-client';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { TooltipProps } from 'recharts';
import { mapPackageTypeName } from '../../../../utils/mapPackageTypeName';
import type { Coordinate } from 'recharts/types/util/types';
import { useState } from 'react';
import { SalesProCard } from '../../../SharedComponents/SalesProCard';
import { PACKAGE_COLORS } from '../../../SharedComponents/Map/Dots/Dots.consts';
import { Box, Label, LabeledControl, LoadingMask, Paragraph, Radio, Table, Text } from '@zillow/constellation-10';
import { useQuery } from '@zg-rentals/react-query5';
import { isAllSingleFamily } from '../../../../utils/isAllSingleFamily';
import { MarketDropdown } from '../../../SharedComponents/CustomerAnalyticsPage/MarketDropdown';

interface PieChartData {
  name: string;
  value: number;
  type: ClientAdProductType;

  percent?: number;
  midAngle?: number;
  middleRadius?: number;
  tooltipPosition?: Coordinate;
  paddingAngle?: number;
  dataKey?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

interface LabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  index: number;
  fill: string;
}

const RADIAN = Math.PI / 180;
const THRESHOLD = 0.05; // 5%

// Helper function to create a label renderer with access to the full dataset
const createLabelRenderer = (data: Array<PieChartData>, totalValue: number) => {
  // Identify which slices are small and adjacent to other small slices
  const isSmallSlice = (idx: number) => {
    if (idx < 0 || idx >= data.length || totalValue === 0) return false;
    const percent = data[idx].value / totalValue;
    return percent < THRESHOLD;
  };

  // Pre-calculate which slices will show inside labels
  const willShowInside = (idx: number) => {
    if (idx < 0 || idx >= data.length) return false;
    const isSmall = isSmallSlice(idx);
    if (!isSmall) return false;
    const prevIsSmall = isSmallSlice(idx - 1);
    const nextIsSmall = isSmallSlice(idx + 1);
    const hasAdjacentSmall = prevIsSmall || nextIsSmall;
    return hasAdjacentSmall && idx % 2 === 1;
  };

  // Count consecutive inside labels
  const getInsideLabelGroupIndex = (idx: number) => {
    if (!willShowInside(idx)) return -1;

    let groupStart = idx;
    while (groupStart > 0 && willShowInside(groupStart - 1)) {
      groupStart--;
    }

    return idx - groupStart;
  };

  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, fill }: LabelProps) => {
    const currentIsSmall = percent < THRESHOLD;

    if (currentIsSmall) {
      // Check if adjacent slices are also small
      const prevIsSmall = isSmallSlice(index - 1);
      const nextIsSmall = isSmallSlice(index + 1);
      const hasAdjacentSmall = prevIsSmall || nextIsSmall;

      // For adjacent small slices, alternate between inside and outside placement
      // Odd indices show inside, even indices show outside
      const showInside = hasAdjacentSmall && index % 2 === 1;

      if (showInside) {
        // Show label inside the center area with guide line
        const insideGroupIndex = getInsideLabelGroupIndex(index);

        // Use much larger radius variations to prevent overlap
        // Pattern creates distinct spacing: 0.25, 0.5, 0.75 of innerRadius
        let radiusMultiplier = 0.5;
        if (insideGroupIndex >= 0) {
          const pattern = insideGroupIndex % 3;
          radiusMultiplier = 0.25 + pattern * 0.25;
        }

        const labelRadius = innerRadius * radiusMultiplier;
        const lineStartRadius = innerRadius;
        // Add gap between line end and label (15 pixels)
        const gap = 15;
        const lineEndRadius = labelRadius + gap;

        const labelX = cx + labelRadius * Math.cos(-midAngle * RADIAN);
        const labelY = cy + labelRadius * Math.sin(-midAngle * RADIAN);

        const lineStartX = cx + lineStartRadius * Math.cos(-midAngle * RADIAN);
        const lineStartY = cy + lineStartRadius * Math.sin(-midAngle * RADIAN);

        const lineEndX = cx + lineEndRadius * Math.cos(-midAngle * RADIAN);
        const lineEndY = cy + lineEndRadius * Math.sin(-midAngle * RADIAN);

        return (
          <g>
            {/* Guide line from inner edge toward label with gap */}
            <line x1={lineStartX} y1={lineStartY} x2={lineEndX} y2={lineEndY} stroke={fill} strokeWidth={2} />
            <text
              x={labelX}
              y={labelY}
              fill={fill}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={20}
              fontWeight="bold"
            >
              {index + 1}
            </text>
          </g>
        );
      } else {
        // Show label outside with a line
        const lineEndRadius = outerRadius + 25;
        const labelRadius = outerRadius + 35;

        const lineEndX = cx + lineEndRadius * Math.cos(-midAngle * RADIAN);
        const lineEndY = cy + lineEndRadius * Math.sin(-midAngle * RADIAN);

        const labelX = cx + labelRadius * Math.cos(-midAngle * RADIAN);
        const labelY = cy + labelRadius * Math.sin(-midAngle * RADIAN);

        // Calculate the starting point of the line (on the outer edge of the slice)
        const lineStartX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
        const lineStartY = cy + outerRadius * Math.sin(-midAngle * RADIAN);

        return (
          <g>
            {/* Custom line in slice color */}
            <line x1={lineStartX} y1={lineStartY} x2={lineEndX} y2={lineEndY} stroke={fill} strokeWidth={2} />
            <text
              x={labelX}
              y={labelY}
              fill={fill}
              textAnchor={labelX > cx ? 'start' : 'end'}
              dominantBaseline="central"
              fontSize={20}
              fontWeight="bold"
            >
              {index + 1}
            </text>
          </g>
        );
      }
    } else {
      // For larger slices, show label inside
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={20} fontWeight="bold">
          {index + 1}
        </text>
      );
    }
  };

  renderLabel.displayName = 'PieChartCustomLabel';
  return renderLabel;
};

// Custom tooltip component that formats values to 2 decimal places
const createCustomTooltip = (elementsCount: number) => {
  const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percent = elementsCount > 0 && data.value ? (data.value / elementsCount) * 100 : 0;
      return (
        <Box
          css={{
            backgroundColor: 'white',
            padding: '400',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: '200',
          }}
        >
          <Text textStyle="body-sm-bold">{data.name}</Text> <Text textStyle="body-sm">{percent.toFixed(2)}%</Text>
        </Box>
      );
    }
    return null;
  };

  CustomTooltip.displayName = 'CustomTooltip';
  return CustomTooltip;
};

// Helper function to map AnalyticsProductTier to ClientAdProductType
const mapTierToProductType = (tier: AnalyticsProductTier): ClientAdProductType => {
  switch (tier) {
    case 'BASE':
      return ClientAdProductType.BASE_PACKAGE;
    case 'ENHANCED':
      return ClientAdProductType.ENHANCED_PACKAGE;
    case 'PREMIUM':
      return ClientAdProductType.PREMIUM_PACKAGE;
    case 'PREMIUM_PLUS':
      return ClientAdProductType.PREMIUM_PLUS_PACKAGE;
    case 'SIGNATURE':
      return ClientAdProductType.SIGNATURE_PACKAGE;
    default:
      return ClientAdProductType.BASE_PACKAGE;
  }
};

export const AdSpendSidebar = () => {
  const { filteredProperties, selectedMarketId } = useMainLayout();
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const currentMothProperties = filteredProperties.filter((property) =>
    property.breakdownStats.some(
      (stat) => stat.statDate?.month === currentMonth && stat.statDate?.year === currentYear,
    ),
  );

  const [viewBy, setViewBy] = useState<'Portfolio' | 'Market'>('Portfolio');

  const { data: marketPackageDistribution, isLoading: isLoadingMarketData } = useQuery({
    queryKey: [MarketAnalyticsWebApiHandler_getMarketPackageDistribution_Path, { selectedMarketId, viewBy }],
    queryFn: async () =>
      MarketAnalyticsWebApiHandler_getMarketPackageDistribution({
        zillowRegionId: selectedMarketId,
      }),
    enabled: viewBy === 'Market',
  });

  // Portfolio pie chart data (existing logic)
  const portfolioPieChartData: Array<PieChartData> = currentMothProperties.reduce(
    (acc: Array<PieChartData>, property) => {
      if (property.aggregateStats.adproductTypes.length > 0 && property.aggregateStats.adproductTypes[0]) {
        const type = property.aggregateStats.adproductTypes[0];
        const currentType = acc.find((item) => item.name === mapPackageTypeName(type));
        if (currentType) {
          currentType.value += 1;
        } else {
          acc.push({ name: mapPackageTypeName(type), value: 1, type });
        }
      }
      return acc;
    },
    [],
  );

  // Market pie chart data (from API)
  const marketPieChartData: Array<PieChartData> = (marketPackageDistribution || []).map((item) => {
    const clientAdProductType = mapTierToProductType(item.productTier);
    return {
      name: mapPackageTypeName(clientAdProductType),
      value: item.distributionPercentage,
      type: clientAdProductType,
    };
  });

  // Use market data when viewBy is 'Market', otherwise use portfolio data
  const pieChartData = viewBy === 'Market' && !isLoadingMarketData ? marketPieChartData : portfolioPieChartData;

  const elementsCount = pieChartData.reduce((acc, curr) => acc + curr.value, 0);

  // Sort and distribute slices to prevent more than 3 consecutive small slices
  const sortOrder = ['Base', 'Enhanced', 'Premium', 'Signature', 'Unadvertised'];
  const initialSorted = [...pieChartData].sort((a, b) => {
    const aIndex = sortOrder.findIndex((name) => a.name.includes(name));
    const bIndex = sortOrder.findIndex((name) => b.name.includes(name));
    return aIndex - bIndex;
  });

  // Separate into large and small slices
  const largeSlices: Array<PieChartData> = [];
  const smallSlices: Array<PieChartData> = [];

  initialSorted.forEach((slice) => {
    const percent = elementsCount > 0 ? slice.value / elementsCount : 0;
    if (percent < THRESHOLD) {
      smallSlices.push(slice);
    } else {
      largeSlices.push(slice);
    }
  });

  // Redistribute to ensure no more than 2 consecutive small slices
  const sortedPieChartData: Array<PieChartData> = [];
  let smallIndex = 0;
  let largeIndex = 0;
  let consecutiveSmall = 0;

  while (smallIndex < smallSlices.length || largeIndex < largeSlices.length) {
    // If we have 2 consecutive small slices, force a large slice
    if (consecutiveSmall >= 2 && largeIndex < largeSlices.length) {
      sortedPieChartData.push(largeSlices[largeIndex++]);
      consecutiveSmall = 0;
    }
    // If we only have small slices left, add them
    else if (largeIndex >= largeSlices.length && smallIndex < smallSlices.length) {
      sortedPieChartData.push(smallSlices[smallIndex++]);
      consecutiveSmall++;
    }
    // If we only have large slices left, add them
    else if (smallIndex >= smallSlices.length && largeIndex < largeSlices.length) {
      sortedPieChartData.push(largeSlices[largeIndex++]);
      consecutiveSmall = 0;
    }
    // Otherwise, alternate strategically
    else {
      // Add small slices in groups of up to 2, then add a large slice
      if (consecutiveSmall < 2 && smallIndex < smallSlices.length) {
        sortedPieChartData.push(smallSlices[smallIndex++]);
        consecutiveSmall++;
      } else if (largeIndex < largeSlices.length) {
        sortedPieChartData.push(largeSlices[largeIndex++]);
        consecutiveSmall = 0;
      }
    }
  }

  const shouldHidePieChart = isAllSingleFamily(currentMothProperties) || currentMothProperties.length === 0;

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
      <Box>
        <Box css={{ paddingInline: '400' }}>
          <Paragraph css={{ marginBottom: '400' }}>
            Your advertising&apos;s giving your portfolio more exposure compared to others in this market.
          </Paragraph>
          {!shouldHidePieChart ? (
            <Box css={{ display: 'flex', gap: '400', marginBottom: '400', alignItems: 'center' }}>
              <Text textStyle="body-sm-bold">View by:</Text>
              <Box css={{ display: 'flex', gap: '400' }}>
                <LabeledControl
                  control={
                    <Radio
                      checked={viewBy === 'Portfolio'}
                      name="view-by"
                      onChange={(e) => e.target.checked && setViewBy('Portfolio')}
                      value="Portfolio"
                    />
                  }
                  label={<Label>Portfolio</Label>}
                />
                <LabeledControl
                  control={
                    <Radio
                      checked={viewBy === 'Market'}
                      name="view-by"
                      onChange={(e) => e.target.checked && setViewBy('Market')}
                      value="Market"
                    />
                  }
                  label={<Label>Market</Label>}
                />
              </Box>
            </Box>
          ) : null}
        </Box>
        {!shouldHidePieChart ? (
          <LoadingMask loading={viewBy === 'Market' && isLoadingMarketData} css={{ display: 'block' }}>
            {sortedPieChartData.length === 0 && (viewBy !== 'Market' || !isLoadingMarketData) ? (
              <Box css={{ padding: '800', textAlign: 'center' }}>
                <Paragraph>No ad package distribution data available.</Paragraph>
              </Box>
            ) : (
              <>
                <Box css={{ position: 'relative' }}>
                  {viewBy === 'Market' && (
                    <Box css={{ marginInline: '400' }}>
                      <MarketDropdown hideLine />
                    </Box>
                  )}
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={sortedPieChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius="40%"
                        outerRadius="70%"
                        label={createLabelRenderer(sortedPieChartData, elementsCount)}
                        labelLine={false}
                        isAnimationActive={false}
                      >
                        {sortedPieChartData.map((entry) => (
                          <Cell key={`cell-${entry.name}`} fill={PACKAGE_COLORS[entry.type]} />
                        ))}
                      </Pie>
                      <Tooltip content={createCustomTooltip(elementsCount)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Box css={{ marginInline: '400', marginTop: '400' }}>
                  <Table.Root aria-label="Ad package distribution table" appearance="horizontal">
                    <Table.Body>
                      {sortedPieChartData.map((entry, index) => {
                        const percent = elementsCount > 0 ? (entry.value / elementsCount) * 100 : 0;
                        const packageName = entry.name.replace(' Package', '');
                        return (
                          <Table.Row key={entry.name}>
                            <Table.Cell>
                              <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
                                <Box
                                  css={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                  style={{ backgroundColor: PACKAGE_COLORS[entry.type] }}
                                >
                                  <Text textStyle="body-sm-bold" css={{ color: 'white' }}>
                                    {index + 1}
                                  </Text>
                                </Box>
                                <Text textStyle="body">{packageName}</Text>
                              </Box>
                            </Table.Cell>
                            <Table.Cell css={{ textAlign: 'right' }}>
                              <Text textStyle="body-bold">{percent.toFixed(2)}%</Text>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table.Root>
                </Box>
              </>
            )}
          </LoadingMask>
        ) : null}
      </Box>
      <SalesProCard
        title="Tailor your ad mix to perfection"
        description="Talk to your sales pro about calibrating your advertising to stay competitive. "
        buttonText="Connect with [sales pro name]"
      />
    </Box>
  );
};
